import { useListingStepNavigation } from '@/lib/hooks/use-listing-step-navigation'
import { useState, useCallback, useEffect, useRef } from 'react'
import { View, Text, ActivityIndicator, ScrollView } from 'react-native'
import { useTranslation } from 'react-i18next'
import Animated from 'react-native-reanimated'
import { Clock3, DollarSign, ShieldCheck, TrendingUp } from '@/components/ui/icons'
import { selection } from '@/lib/utils/haptics'
import { Button } from '@/components/ui/Button'
import { TextInput } from '@/components/ui/TextInput'
import { KeyboardAwareView } from '@/components/ui/KeyboardAwareView'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { FormActionBar } from '@/components/ui/FormActionBar'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { MAX_SECURITY_DEPOSIT_CENTS, vehiclePricingSchema } from '@/lib/validations/vehicle.schema'
import { useSemanticColors, SPACING, RADIUS } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { vehicleAIService, type PricingSuggestion } from '@/lib/services/vehicle-ai.service'
import type { VehicleMinRentalHours } from '@/lib/utils/min-rental-duration'
import { dollarsToCents, formatCurrency } from '@/lib/utils/format'
import { useEntrance } from '@/lib/hooks/use-entrance'

const parseCents = (text: string): number | undefined => {
  const num = parseFloat(text)
  if (isNaN(num)) return undefined
  return dollarsToCents(num)
}

/** Plain decimal string for the TextInput value — no "$"/separators so parseCents round-trips. */
const formatDollars = (cents: number | undefined): string => {
  if (cents === undefined) return ''
  return (cents / 100).toFixed(2)
}

const MIN_RENTAL_OPTIONS: Array<{
  value: VehicleMinRentalHours
  titleKey: string
  descriptionKey: string
  testID: string
}> = [
  {
    value: null,
    titleKey: 'listing.min_rental_none',
    descriptionKey: 'listing.min_rental_none_desc',
    testID: 'pricing.min-rental-none',
  },
  {
    value: 48,
    titleKey: 'listing.min_rental_2_days',
    descriptionKey: 'listing.min_rental_2_days_desc',
    testID: 'pricing.min-rental-48',
  },
  {
    value: 72,
    titleKey: 'listing.min_rental_3_days',
    descriptionKey: 'listing.min_rental_3_days_desc',
    testID: 'pricing.min-rental-72',
  },
]

export default function NewListingPricingScreen() {
  const enter = useEntrance()
  const { t } = useTranslation()
  const { continueEditing, continueLabel } = useListingStepNavigation('documents')
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const details = useListingWizardStore((s) => s.details)
  const location = useListingWizardStore((s) => s.location)
  const pricing = useListingWizardStore((s) => s.pricing)
  const setPricing = useListingWizardStore((s) => s.setPricing)
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Validation errors render ABOVE the fold; without this the host taps
  // Siguiente, nothing visibly happens, and the step looks broken.
  const errorScrollRef = useRef<ScrollView>(null)
  const [isDailyRateFocused, setIsDailyRateFocused] = useState(false)
  const [dailyRateText, setDailyRateText] = useState(() => formatDollars(pricing.dailyRateCents))
  const [depositText, setDepositText] = useState(() => formatDollars(pricing.securityDepositCents))
  const selectedMinRentalHours = pricing.minRentalHours ?? null

  // ── Smart pricing suggestion ───────────────────────────────────────────
  const [pricingSuggestion, setPricingSuggestion] = useState<PricingSuggestion | null>(null)
  const [isLoadingPricing, setIsLoadingPricing] = useState(false)

  useEffect(() => {
    if (!details.category || !details.year) return

    let cancelled = false
    setIsLoadingPricing(true)

    vehicleAIService
      .suggestPricing(details.category, details.year, location.cityId)
      .then((result) => {
        if (!cancelled) setPricingSuggestion(result)
      })
      .catch(() => {
        // Suggestion does not block listing intake.
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPricing(false)
      })

    return () => {
      cancelled = true
    }
  }, [details.category, details.year, location.cityId])

  const applyPricingSuggestion = useCallback(() => {
    if (!pricingSuggestion?.suggestedDailyRateCents) return
    void selection()
    setPricing({ dailyRateCents: pricingSuggestion.suggestedDailyRateCents })
    setErrors((prev) => ({ ...prev, dailyRateCents: '' }))
  }, [pricingSuggestion, setPricing])

  const updateField = (key: string, value: unknown) => {
    setPricing({ ...pricing, [key]: value })
    setErrors((prev) => ({ ...prev, [key]: '' }))
  }

  useEffect(() => {
    if (!isDailyRateFocused) {
      setDailyRateText(formatDollars(pricing.dailyRateCents))
    }
  }, [isDailyRateFocused, pricing.dailyRateCents])

  const updateDailyRate = (text: string) => {
    setDailyRateText(text)
    updateField('dailyRateCents', parseCents(text))
  }

  const updateMinRentalHours = (value: VehicleMinRentalHours) => {
    void selection()
    updateField('minRentalHours', value)
  }

  const handleNext = useCallback(() => {
    const input = {
      dailyRateCents: pricing.dailyRateCents,
      hourlyRateCents: pricing.hourlyRateCents,
      weeklyDiscountPercent: pricing.weeklyDiscountPercent,
      monthlyDiscountPercent: pricing.monthlyDiscountPercent,
      securityDepositCents: pricing.securityDepositCents,
      cleaningFeeCents: pricing.cleaningFeeCents,
      dailyKmLimit: pricing.dailyKmLimit || undefined,
      excessKmRateCents: pricing.excessKmRateCents || undefined,
      minRentalHours: pricing.minRentalHours ?? null,
      // The price form does not offer insurance changes. Preserve the loaded
      // model, so visiting Price cannot quietly change an existing car's cover.
      insuranceModel: pricing.insuranceModel ?? 'fleet',
    }

    const result = vehiclePricingSchema.safeParse(input)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      for (const issue of result.error.issues) {
        const field = issue.path[0] as string
        fieldErrors[field] = t(issue.message)
      }
      setErrors(fieldErrors)
      requestAnimationFrame(() => errorScrollRef.current?.scrollTo({ y: 0, animated: true }))
      return
    }

    setPricing(result.data)
    continueEditing()
  }, [pricing, setPricing, continueEditing, t])

  return (
    <ScreenContainer variant="content" scroll={false}>
      <KeyboardAwareView
        includeTopInset={false}
        scrollRef={errorScrollRef}
        contentContainerStyle={{
          padding: SPACING.lg,
          // The action bar is a sibling below this ScrollView, not an overlay
          // on top of it, so the old ~120pt of trailing padding was scrollable
          // void under the last card rather than clearance for the button.
          paddingBottom: SPACING.xl,
        }}
      >
        {/* Smart Pricing Suggestion */}
        {isLoadingPricing || pricingSuggestion?.suggestedDailyRateCents ? (
          <Animated.View
            collapsable={false}
            entering={enter(1)}
            style={{ marginBottom: SPACING.md }}
          >
            <View
              style={{
                backgroundColor: `${COLORS.coral.DEFAULT}08`,
                borderWidth: 1,
                borderColor: `${COLORS.coral.DEFAULT}20`,
                borderRadius: RADIUS.lg,
                padding: SPACING.md,
              }}
            >
              {isLoadingPricing ? (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
                  <ActivityIndicator size="small" color={COLORS.coral.DEFAULT} />
                  <Text
                    style={{ fontSize: 14, fontFamily: FONTS.body, color: colors.textSecondary }}
                  >
                    {t('common.loading')}
                  </Text>
                </View>
              ) : pricingSuggestion?.suggestedDailyRateCents ? (
                <>
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.sm,
                      marginBottom: SPACING.sm,
                    }}
                  >
                    <TrendingUp size={16} color={COLORS.coral.DEFAULT} />
                    <Text
                      style={{
                        fontSize: 14,
                        fontFamily: FONTS.body,
                        color: colors.textPrimary,
                      }}
                    >
                      {t('listing.ai_suggested_price')}
                    </Text>
                  </View>

                  {/* Price range bar */}
                  <View
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: SPACING.sm,
                      marginBottom: SPACING.sm,
                    }}
                  >
                    <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                      {t('listing.ai_price_budget')}
                    </Text>
                    <View
                      style={{
                        flex: 1,
                        height: 6,
                        backgroundColor: colors.surfaceSubtle,
                        borderRadius: RADIUS.full,
                        overflow: 'hidden',
                      }}
                    >
                      <View
                        style={{
                          position: 'absolute',
                          left: '20%',
                          right: '20%',
                          height: 6,
                          backgroundColor: COLORS.coral.DEFAULT,
                          borderRadius: RADIUS.full,
                          opacity: 0.6,
                        }}
                      />
                    </View>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textMuted }}>
                      {t('listing.ai_price_premium')}
                    </Text>
                  </View>

                  <View
                    style={{
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <View>
                      <Text
                        style={{
                          fontSize: 20,
                          fontFamily: FONTS.bodyBold,
                          color: colors.textAccent,
                        }}
                      >
                        {formatCurrency(pricingSuggestion.suggestedDailyRateCents)}
                        {t('vehicle_detail.per_day')}
                      </Text>
                      <Text
                        style={{
                          fontSize: 12,
                          fontFamily: FONTS.body,
                          color: colors.textMuted,
                          marginTop: 2,
                        }}
                      >
                        {t('listing.ai_price_based_on', { count: pricingSuggestion.count })}
                      </Text>
                    </View>
                    <Pressable
                      onPress={applyPricingSuggestion}
                      accessibilityRole="button"
                      accessibilityLabel={t('listing.ai_use_suggestion')}
                      testID="pricing.use-suggestion"
                      style={({ pressed }) => ({
                        backgroundColor: COLORS.coral.dark,
                        paddingHorizontal: SPACING.md,
                        paddingVertical: SPACING.sm,
                        // 44pt minimum: the chip was ~33pt, small enough that a
                        // thumb aiming for it lands just outside and nothing
                        // happens — which reads as "the app ignored me".
                        minHeight: 44,
                        justifyContent: 'center',
                        borderRadius: RADIUS.md,
                        opacity: pressed ? 0.82 : 1,
                      })}
                    >
                      <Text
                        style={{ fontSize: 13, fontFamily: FONTS.body, color: '#FFFFFF' }}
                        numberOfLines={1}
                        adjustsFontSizeToFit
                      >
                        {t('listing.ai_use_suggestion')}
                      </Text>
                    </Pressable>
                  </View>
                </>
              ) : null}
            </View>
          </Animated.View>
        ) : null}

        <Animated.View
          collapsable={false}
          entering={enter(isLoadingPricing || pricingSuggestion ? 2 : 1)}
        >
          {/* Currency affix. This is the one number the whole step exists to
              collect, and it was an unmarked box the same size as any other
              field on a screen with room to spare. */}
          <TextInput
            label={t('listing.daily_rate')}
            leftIcon={<DollarSign size={18} strokeWidth={2} />}
            value={dailyRateText}
            onFocus={() => setIsDailyRateFocused(true)}
            onBlur={() => {
              setIsDailyRateFocused(false)
              setDailyRateText(formatDollars(pricing.dailyRateCents))
            }}
            onChangeText={updateDailyRate}
            keyboardType="decimal-pad"
            placeholder={t('listing.daily_rate_placeholder')}
            error={errors.dailyRateCents}
            testID="pricing.daily-rate"
          />
        </Animated.View>

        <Animated.View collapsable={false} entering={enter(2)}>
          {/* Optional security deposit. Charged to the guest, held by Tago, and
              returned in full after the trip. The host absorbs the ~3.5%
              processing cost on return — surfaced in the help text so it is
              never a surprise on the payout. */}
          <View style={{ marginTop: SPACING.md }}>
            <TextInput
              label={t('listing.security_deposit')}
              leftIcon={<ShieldCheck size={18} strokeWidth={2} />}
              value={depositText}
              onBlur={() => setDepositText(formatDollars(pricing.securityDepositCents))}
              onChangeText={(text) => {
                // Clamp at the cap instead of letting the host type $600 and
                // meet a rejection at the end. Apple-level means the wrong value
                // is not reachable, not that the error is well worded.
                const parsed = parseCents(text)
                if (parsed !== undefined && parsed > MAX_SECURITY_DEPOSIT_CENTS) {
                  setDepositText(formatDollars(MAX_SECURITY_DEPOSIT_CENTS))
                  updateField('securityDepositCents', MAX_SECURITY_DEPOSIT_CENTS)
                  return
                }
                setDepositText(text)
                updateField('securityDepositCents', parsed)
              }}
              keyboardType="decimal-pad"
              placeholder={t('listing.security_deposit_placeholder')}
              error={errors.securityDepositCents}
              testID="pricing.security-deposit"
            />
            <Text
              style={{
                marginTop: SPACING.xs,
                fontSize: 12,
                lineHeight: 17,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
              }}
            >
              {t('listing.security_deposit_help')}
            </Text>
          </View>
        </Animated.View>

        <Animated.View collapsable={false} entering={enter(3)}>
          {/* This step carries two blocks on a screen sized for five, so the
              card takes the generous padding rather than letting all the slack
              pool at the bottom of the scroll. */}
          <View
            style={{
              marginTop: SPACING.md,
              borderRadius: RADIUS.lg,
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: colors.surface,
              padding: SPACING.lg,
            }}
          >
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm,
                marginBottom: SPACING.xs,
              }}
            >
              <View
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 15,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: `${COLORS.coral.DEFAULT}12`,
                }}
              >
                <Clock3 size={16} color={COLORS.coral.DEFAULT} strokeWidth={2.2} />
              </View>
              <Text
                style={{
                  flex: 1,
                  fontSize: 16,
                  lineHeight: 21,
                  fontFamily: FONTS.body,
                  color: colors.textPrimary,
                }}
              >
                {t('listing.min_rental_duration')}
              </Text>
            </View>

            <Text
              style={{
                fontSize: 13,
                lineHeight: 18,
                fontFamily: FONTS.body,
                color: colors.textSecondary,
                marginBottom: SPACING.md,
              }}
            >
              {t('listing.min_rental_duration_desc')}
            </Text>

            <View style={{ gap: SPACING.sm }} testID="pricing.min-rental-options">
              {MIN_RENTAL_OPTIONS.map((option) => {
                const selected = selectedMinRentalHours === option.value
                return (
                  <Pressable
                    key={option.testID}
                    onPress={() => updateMinRentalHours(option.value)}
                    accessibilityRole="radio"
                    accessibilityState={{ checked: selected }}
                    accessibilityLabel={t(option.titleKey)}
                    testID={option.testID}
                    style={({ pressed }) => ({
                      minHeight: 58,
                      borderRadius: RADIUS.md,
                      borderWidth: 1,
                      borderColor: selected ? COLORS.coral.DEFAULT : colors.border,
                      backgroundColor: selected ? `${COLORS.coral.DEFAULT}10` : colors.background,
                      paddingHorizontal: SPACING.md,
                      paddingVertical: SPACING.sm,
                      justifyContent: 'center',
                      opacity: pressed ? 0.82 : 1,
                    })}
                  >
                    <View
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: SPACING.md,
                      }}
                    >
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text
                          style={{
                            fontSize: 15,
                            lineHeight: 20,
                            fontFamily: FONTS.body,
                            color: selected ? colors.textAccent : colors.textPrimary,
                          }}
                          numberOfLines={1}
                        >
                          {t(option.titleKey)}
                        </Text>
                        <Text
                          style={{
                            marginTop: 2,
                            fontSize: 12,
                            lineHeight: 17,
                            fontFamily: FONTS.body,
                            color: colors.textSecondary,
                          }}
                          numberOfLines={2}
                        >
                          {t(option.descriptionKey)}
                        </Text>
                      </View>
                      <View
                        style={{
                          width: 18,
                          height: 18,
                          borderRadius: 9,
                          borderWidth: 1,
                          borderColor: selected ? COLORS.coral.DEFAULT : colors.border,
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: selected ? COLORS.coral.DEFAULT : 'transparent',
                        }}
                      >
                        {selected ? (
                          <View
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#FFFFFF',
                            }}
                          />
                        ) : null}
                      </View>
                    </View>
                  </Pressable>
                )
              })}
            </View>

            {errors.minRentalHours ? (
              <Text
                style={{
                  marginTop: SPACING.sm,
                  fontSize: 12,
                  lineHeight: 17,
                  fontFamily: FONTS.body,
                  color: colors.textError,
                }}
              >
                {errors.minRentalHours}
              </Text>
            ) : null}
          </View>
        </Animated.View>
      </KeyboardAwareView>

      <FormActionBar style={{ backgroundColor: colors.background }}>
        <Button
          onPress={handleNext}
          variant="primary"
          size="lg"
          title={continueLabel}
          testID="pricing.next-button"
        />
      </FormActionBar>
    </ScreenContainer>
  )
}
